import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload, FileText, Download, RefreshCw, MessageSquare, AlertTriangle, AlertCircle, Info, X, Edit } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Remove old token function - now using global Firebase auth via axios interceptor

const getSeverityIcon = (severity) => {
  switch (severity) {
    case 'high':
      return <AlertTriangle size={16} className="text-red-500" />;
    case 'medium':
      return <AlertCircle size={16} className="text-yellow-500" />;
    case 'low':
      return <Info size={16} className="text-blue-500" />;
    default:
      return <MessageSquare size={16} />;
  }
};

const getCategoryLabel = (category) => {
  const labels = {
    legal_risk: 'Legal Risk',
    unclear: 'Unclear Clause',
    missing_clause: 'Missing Clause',
    unfavorable: 'Unfavorable Term',
    grammar: 'Grammar/Formatting'
  };
  return labels[category] || category;
};

const InlineDocumentReview = ({ onBack }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [editableContent, setEditableContent] = useState('');
  const [selectedComment, setSelectedComment] = useState(null);
  const [reReviewing, setReReviewing] = useState(false);
  const editorRef = useRef(null);

  // Set page title
  useEffect(() => {
    document.title = "Inline Document Review - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  useEffect(() => {
    if (selectedComment && editorRef.current && documentData) {
      highlightSelectedText();
    }
  }, [selectedComment]);

  const highlightSelectedText = () => {
    if (!editorRef.current || !selectedComment) return;

    // Remove any existing highlights
    const existingHighlights = editorRef.current.querySelectorAll('.selected-comment-highlight');
    existingHighlights.forEach(el => {
      const parent = el.parentNode;
      parent.replaceChild(document.createTextNode(el.textContent), el);
      parent.normalize();
    });

    // Get the text content from the editor
    const editorText = editorRef.current.innerText;
    
    // Find the text to highlight
    const textToHighlight = selectedComment.highlighted_text;
    const startIndex = editorText.indexOf(textToHighlight);
    
    if (startIndex === -1) return;

    // Create a selection range
    const range = document.createRange();
    const selection = window.getSelection();
    
    // Find the text node and position
    let currentIndex = 0;
    let startNode = null;
    let startOffset = 0;
    let endNode = null;
    let endOffset = 0;
    
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let node;
    while ((node = walker.nextNode())) {
      const nodeLength = node.textContent.length;
      
      if (startNode === null && currentIndex + nodeLength > startIndex) {
        startNode = node;
        startOffset = startIndex - currentIndex;
      }
      
      if (startNode !== null && currentIndex + nodeLength >= startIndex + textToHighlight.length) {
        endNode = node;
        endOffset = startIndex + textToHighlight.length - currentIndex;
        break;
      }
      
      currentIndex += nodeLength;
    }
    
    if (startNode && endNode) {
      try {
        range.setStart(startNode, startOffset);
        range.setEnd(endNode, endOffset);
        
        // Create highlight span
        const highlightSpan = document.createElement('span');
        highlightSpan.className = 'selected-comment-highlight';
        highlightSpan.style.backgroundColor = 'rgba(239, 68, 68, 0.3)';
        highlightSpan.style.padding = '2px 0';
        highlightSpan.style.borderRadius = '2px';
        
        range.surroundContents(highlightSpan);
        
        // Scroll the highlight into view
        highlightSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        console.error('Error highlighting text:', e);
      }
    }
  };

  const clearHighlights = () => {
    if (!editorRef.current) return;
    
    const existingHighlights = editorRef.current.querySelectorAll('.selected-comment-highlight');
    existingHighlights.forEach(el => {
      const parent = el.parentNode;
      parent.replaceChild(document.createTextNode(el.textContent), el);
      parent.normalize();
    });
  };

  const handleCommentSelect = (comment) => {
    setSelectedComment(comment);
  };

  const handleCommentDeselect = () => {
    clearHighlights();
    setSelectedComment(null);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size exceeds 10MB limit');
        return;
      }
      if (!selectedFile.name.toLowerCase().endsWith('.docx')) {
        toast.error('Only DOCX files are supported');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/inline-review/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setDocumentData(response.data);
      setEditableContent(response.data.original_content);
      toast.success('Document analyzed successfully!');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Error analyzing document');
    } finally {
      setUploading(false);
    }
  };

  const handleContentChange = async (newContent) => {
    setEditableContent(newContent);
    
    // Debounce save to backend
    if (documentData) {
      try {
        await axios.put(
          `${API}/inline-review/${documentData.document_id}`,
          { content: newContent }
        );
      } catch (error) {
        console.error('Error saving changes:', error);
      }
    }
  };

  const handleReReview = async () => {
    if (!documentData) return;

    setReReviewing(true);
    try {
      const response = await axios.post(
        `${API}/inline-review/${documentData.document_id}/re-review`,
        {}
      );

      setDocumentData(response.data);
      setEditableContent(response.data.original_content);
      toast.success('Document re-analyzed successfully!');
    } catch (error) {
      console.error('Error re-reviewing document:', error);
      toast.error(error.response?.data?.detail || 'Error re-analyzing document');
    } finally {
      setReReviewing(false);
    }
  };

  const handleDownload = async () => {
    if (!documentData || !editableContent) return;

    try {
      const response = await axios.post(
        `${API}/inline-review/${documentData.document_id}/download`,
        { 
          edited_content: editableContent 
        },
        {
          responseType: 'blob'
        }
      );

      // Get original filename or use file name
      let filename = file?.name || 'document.docx';
      
      // Ensure .docx extension and keep original filename
      if (!filename.endsWith('.docx')) {
        const nameWithoutExt = filename.replace(/\.[^.]*$/, '');
        filename = nameWithoutExt + '.docx';
      }

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error downloading document');
    }
  };

  const handleReset = () => {
    setFile(null);
    setDocumentData(null);
    setEditableContent('');
    setSelectedComment(null);
    if (document.getElementById('file-input')) {
      document.getElementById('file-input').value = '';
    }
  };

  const renderHighlightedContent = () => {
    if (!documentData || !editableContent) return editableContent;

    const sortedComments = [...documentData.comments].sort((a, b) => a.start_index - b.start_index);
    let result = [];
    let lastIndex = 0;

    sortedComments.forEach((comment, idx) => {
      // Add text before highlight
      if (comment.start_index > lastIndex) {
        result.push(editableContent.substring(lastIndex, comment.start_index));
      }

      // Add highlighted text
      const highlightClass = `highlight-${comment.severity} ${selectedComment?.id === comment.id ? 'selected' : ''}`;
      result.push(
        <span
          key={idx}
          className={highlightClass}
          onClick={() => setSelectedComment(comment)}
          style={{ cursor: 'pointer' }}
        >
          {editableContent.substring(comment.start_index, comment.end_index)}
        </span>
      );

      lastIndex = comment.end_index;
    });

    // Add remaining text
    if (lastIndex < editableContent.length) {
      result.push(editableContent.substring(lastIndex));
    }

    return result;
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <Edit size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Inline Review & Editor" />
            <p className="tool-subtitle">AI-powered document review with inline comments and editing</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!documentData ? (
          <>
            {/* Upload Card */}
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Inline Review & Editor</CardTitle>
                <CardDescription>
                  Upload a legal document and get AI-powered inline comments highlighting risks, issues, and improvements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="upload-label">Legal Document (DOCX) *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file-input"
                      accept=".docx"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-input" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {file ? <FileText size={48} /> : <Upload size={48} />}
                      </div>
                      <div className="upload-text">
                        {file ? (
                          <>
                            <p className="file-name">{file.name}</p>
                            <p className="change-file">Click to change file</p>
                          </>
                        ) : (
                          <>
                            <p>Click to upload document (DOCX)</p>
                            <p className="upload-hint">Maximum file size: 10MB</p>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    loading={uploading}
                    size="lg"
                  >
                    Analyse Document
                  </AnimatedActionButton>
                  
                  <div className={`led-progress-container ${uploading ? 'active' : ''}`}>
                    <div className="led-progress-line">
                      <div className="led-progress-glow"></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Review Results */}
            <div className="results-header">
              <h2>Document Review</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button onClick={handleReReview} variant="outline" disabled={reReviewing}>
                  <RefreshCw size={18} />
                  Re-Review
                </Button>
                <Button onClick={handleDownload} variant="outline">
                  <Download size={18} />
                  Download
                </Button>
                <Button onClick={handleReset} variant="outline">
                  <ArrowLeft size={18} />
                  New Review
                </Button>
              </div>
            </div>

            {/* Summary */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>AI Analysis Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p>{documentData.summary}</p>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div className="stat-badge">
                    <span className="stat-value">{documentData.comments.length}</span>
                    <span className="stat-label">Issues Found</span>
                  </div>
                  <div className="stat-badge high">
                    <span className="stat-value">
                      {documentData.comments.filter(c => c.severity === 'high').length}
                    </span>
                    <span className="stat-label">High Severity</span>
                  </div>
                  <div className="stat-badge medium">
                    <span className="stat-value">
                      {documentData.comments.filter(c => c.severity === 'medium').length}
                    </span>
                    <span className="stat-label">Medium Severity</span>
                  </div>
                  <div className="stat-badge low">
                    <span className="stat-value">
                      {documentData.comments.filter(c => c.severity === 'low').length}
                    </span>
                    <span className="stat-label">Low Severity</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Document Editor with Comments */}
            <div className="editor-container">
              <div className="editor-section">
                <Card className="result-card">
                  <CardHeader>
                    <CardTitle>Document Content (Editable)</CardTitle>
                    <CardDescription>Click on highlighted text to view comments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="document-editor"
                      contentEditable
                      ref={editorRef}
                      onInput={(e) => handleContentChange(e.currentTarget.innerHTML)}
                      dangerouslySetInnerHTML={{ __html: editableContent }}
                      suppressContentEditableWarning
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="comments-section">
                <Card className="result-card">
                  <CardHeader>
                    <CardTitle>Comments ({documentData.comments.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="comments-list">
                      {selectedComment ? (
                        <div className={`comment-card selected ${selectedComment.severity}`}>
                          <div className="comment-header">
                            {getSeverityIcon(selectedComment.severity)}
                            <span className="comment-category">{getCategoryLabel(selectedComment.category)}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={handleCommentDeselect}
                              style={{ marginLeft: 'auto' }}
                            >
                              <X size={16} />
                            </Button>
                          </div>
                          <div className="comment-text-highlight">
                            "{selectedComment.highlighted_text}"
                          </div>
                          <div className="comment-body">
                            {selectedComment.comment}
                          </div>
                        </div>
                      ) : (
                        documentData.comments.map((comment, idx) => (
                          <div 
                            key={idx} 
                            className={`comment-card ${comment.severity}`}
                            onClick={() => handleCommentSelect(comment)}
                          >
                            <div className="comment-header">
                              {getSeverityIcon(comment.severity)}
                              <span className="comment-category">{getCategoryLabel(comment.category)}</span>
                            </div>
                            <div className="comment-text-highlight">
                              "{comment.highlighted_text.substring(0, 50)}{comment.highlighted_text.length > 50 ? '...' : ''}"
                            </div>
                            <div className="comment-body">
                              {comment.comment.substring(0, 100)}{comment.comment.length > 100 ? '...' : ''}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
        <ReportIssueBar toolName="Inline Document Review" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InlineDocumentReview;
